import React, { FC, useCallback, useEffect, useRef } from 'react';
import './carousel.sass';
import { SlideNavigation } from './components/slide-navigation/slide-navigation';
import Slide from './components/slide/slide';
import { useCarouselState } from './use-carousel-state';
import { useCarouselAutoScroll } from './use-carousel-autoscroll';
import { ISlidePostConfig } from './horizontal-slider-layout';
import { fullWidthFlexBasis } from './consts';
import HighlightSlide from './components/highlight-slide/highlight-slide';
import DoubleSlide from './components/double-slide/double-slide';
import { ISlideWithoutSliderID } from './use-slider-state';
import { IPost } from '../../../../../shared/types/Post';
import { globalConfig } from '../../../../../shared/config/config';
import useListenKeys from '../../../../../shared/hooks/use-listen-keys';
interface ICarouselProps {
  posts: readonly IPost[];
  config: ISlidePostConfig;
  openPopup: (post: IPost) => void;
  onCtaClick: (post: IPost) => void;
  openPostOnEnter: (event: React.KeyboardEvent<HTMLDivElement>, post: IPost) => void;
  isAutoScroll?: boolean;
  bannerHeight?: number;
  onNext?: () => void;
  onPrev?: () => void;
  sliderType?: string;
}

const mapSlidesToAggregateSlides = (slides: ISlideWithoutSliderID[], numberOfSlidesToAggregate: number) =>
  slides.reduce(
    (previousValue: ISlideWithoutSliderID[][], currentValue) => {
      const currentAggregate = previousValue[previousValue.length - 1];
      return currentAggregate.length < numberOfSlidesToAggregate
        ? [...previousValue.slice(0, -1), [...currentAggregate, currentValue]]
        : [...previousValue, [currentValue]];
    },
    [[]]
  );

export const Carousel: FC<ICarouselProps> = ({
  posts,
  config,
  openPopup,
  onCtaClick,
  openPostOnEnter,
  isAutoScroll,
  bannerHeight,
  onNext,
  onPrev,
  sliderType,
}) => {
  const slideshowRef = useRef<HTMLDivElement>(null);

  const postWidth =
    config.postWidth && !isNaN(Number(config.postWidth)) ? Number(config.postWidth) : Number(globalConfig.postWidth);

  const padding =
    config.postPadding && !isNaN(Number(config.postPadding))
      ? Number(config.postPadding)
      : Number(globalConfig.postPadding);

  const totalPostWidth = postWidth + padding;

  useEffect(() => {
    posts.map((post, index) => {
      post.attributes.highlighted = index % 3 === 0;
      return post;
    });
  }, [posts]);

  const {
    translateX,
    setTranslateX,
    marginLeft,
    flexBasis,
    containerSize,
    slides,
    slidesOnTheScreen,
  } = useCarouselState(posts, slideshowRef, totalPostWidth, sliderType);

  const next = () => {
    onNext && onNext();
    setTranslateX(prev => prev + 1);
  };
  const prev = () => {
    onPrev && onPrev();
    setTranslateX(prev => prev - 1);
  };

  useListenKeys(
    [
      {
        keyName: 'ArrowRight',
        keyAction: () => next(),
      },
      {
        keyName: 'ArrowLeft',
        keyAction: () => prev(),
      },
    ],
    false
  );

  useCarouselAutoScroll(next, translateX, config.postDuration, isAutoScroll);

  const transX = containerSize
    ? translateX * (containerSize.width / Math.round(fullWidthFlexBasis / flexBasis)) * -1
    : 0;

  const isReadyToDisplay = containerSize && slides.length !== 0 && flexBasis !== 0;

  const isInEditor = window.location != window.parent.location && document.referrer.includes('tintup');

  const renderSliderByType = useCallback(
    (sliderType?: string) => {
      switch (sliderType) {
        case 'highlight':
          // @ts-ignore
          return mapSlidesToAggregateSlides(slides, 3).map((aggregateSlide, index) => {
            return (
              <HighlightSlide
                id={`${index}`}
                key={index}
                flexBasis={flexBasis}
                posts={aggregateSlide}
                config={config}
                onSlideContentClick={openPopup}
                onImagaClick={openPopup}
                onCtaClick={onCtaClick}
                openPostOnEnter={openPostOnEnter}
                tabIndex={index < posts.length + slidesOnTheScreen && posts.length <= index ? 0 : -1}
              />
            );
          });

        case 'double-row':
          // @ts-ignore
          return mapSlidesToAggregateSlides(slides, 2).map((aggregateSlide, index) => {
            return (
              <DoubleSlide
                id={`${index}`}
                key={index}
                flexBasis={flexBasis}
                upperPost={aggregateSlide[0]}
                bottomPost={aggregateSlide[1] ? aggregateSlide[1] : aggregateSlide[0]}
                config={config}
                onSlideContentClick={openPopup}
                onImagaClick={openPopup}
                onCtaClick={onCtaClick}
                openPostOnEnter={openPostOnEnter}
                tabIndex={index < posts.length + slidesOnTheScreen && posts.length <= index ? 0 : -1}
              />
            );
          });
        default: {
          return slides.map((slide, index) => {
            return (
              <Slide
                id={`${index}`}
                key={index}
                flexBasis={flexBasis}
                post={slide}
                config={config}
                onSlideContentClick={openPopup}
                onImagaClick={openPopup}
                onCtaClick={onCtaClick}
                openPostOnEnter={openPostOnEnter}
                tabIndex={index < posts.length + slidesOnTheScreen && posts.length <= index ? 0 : -1}
              />
            );
          });
        }
      }
    },
    [config, flexBasis, onCtaClick, openPopup, openPostOnEnter, posts.length, slides, slidesOnTheScreen]
  );

  return (
    <div className="carousel-wrapper" style={{ top: bannerHeight }} ref={slideshowRef}>
      <>
        <SlideNavigation onClick={prev} isNext={false} />
        <SlideNavigation onClick={next} isNext={true} />
      </>
      {isReadyToDisplay ? (
        <div
          className={`carousel ${isInEditor ? 'carousel--inside-editor' : ''}`}
          style={{
            transform: `translateX(${transX}px)`,
            marginLeft: `${marginLeft}px`,
          }}
        >
          {renderSliderByType(sliderType)}
        </div>
      ) : null}
    </div>
  );
};
